import { History } from 'history'

export interface RestockingState {
    productsList: ProductsList
    restockingLists: RestockingLists[]
    restockingListPages: RestockingListPages[]
    restockingItemsList: RestockingItemsList[]
    availableRestockLists: AvailableRestockLists[]
    filtersSearchLists: FiltersSearchLists
    selectedFiltersHistory: SearchRestockListsPayload
    user: User
    autoFetchRestockingLists: boolean
    idRestockingType?: number
    hasForkliftAuth?: boolean
    currentCollectRestockingAddress?: CollectRestockingAddress
    acceptsPageWithPausedStatus: boolean
    isRestockAddressValid: boolean
    currentIdList?: number
    currentProductToRestock?: ItemToRestock
    restockingPageDetails?: RestockingPageDetails
    restockingListTypes: RestockingListType[]
    orderCodes: string[]
    restockingConfig?: RestockingConfig
    movingAddresses: MovingAddress[]

}

export interface RootState {
    restocking: RestockingState
}

export interface RestockingConfig {
    dropOff: boolean
    hasUnfit: boolean
    gondolaFirst: boolean
    reportQuantityPerBox: boolean
    isFifo: boolean
    isExactDueDate: boolean
    collectInEmptyGondola: boolean
}

export interface ValidatedBadge {
    user: string
    type: OperationDataTypeEnum | null
    item: ItemsToCollect | ItemToRestock[] | FinishedPage | null
    idRestockingType?: number
    restockingConfig?: RestockingConfig
}

export interface User {
    user: string
    startedPage: StartedPage
    itemsToRestock: ItemToRestock[]
    idRestockingType?: number
    badge: string
    finishedPage: FinishedPage | null
}

interface ItemOption {
    id: number,
    description: string
}
export interface FiltersSearchLists {
    levels: string[]
    priorities: string[]
    sourceTypes: string[]
    status: ItemOption[]
    types: ItemOption[]
}

export interface ProductsListPayload {
    fulfillmentCenterId: number
    shedCode: ShedCode
    sourceType: SourceType
}

export interface ProductsList {
    products: Products[]
    totalSkus: number
    totalDemandHigh: number
    totalDemandLow: number
    totalProgrammed: number
    totalNoCapacity: number
    level?: string
    priority?: string
}

export interface Products {
    productCode: string
    productDescription: string
    restockQuantity: number
    sourceAddresses: SourceAddresses[]
    minimumExpirationDate: Date
    destinationAddress: string
    noReserve: boolean
    idModel: number
    shedCode: string
    street: string
    noCapacity: boolean
    type?: string
    pageNumber?: number
    preDefinedQtyDivergence?: number
}
export interface SourceAddresses {
    address: string
    expirationDate: Date
    quantity: number
}

export interface SetProductsList {
    productsList: RestockingState['productsList'];
}

export enum ShedCode {
    CR = 'Câmara Refrigerada - CR',
    CF = 'Câmara Fria - CF',
    CT = 'Container - CT',
}

export enum SourceType {
    PROGRAMMED = 'Programada',
    DEMAND = 'Demanda',
    TOTAL = 'Total',
}

export enum GenericApiErrors {
    Failed,
}

export interface GenerateListsPayload {
    history: History
    fulfillmentCenterId: number
    quantityPerPage: number
    quantityToRestocking: number
    products: Products[]
    generatedCode: string[]
    sourceType: string
    totalSkus: number
    idType: number
    level?: string
    priority?: string
    shippingDate?: string
    idFulfillmentCenterDestination?: number
}

export enum PageStatusFilters {
    ALL_OPTIONS = 'Todas as opções',
    CLOSED = 'Concluidas',
    OPENED = 'Em aberto',
}

export enum PageStatusFilterIds {
    ALL_OPTIONS = 0,
    CLOSED = 1,
    OPENED = 2,
}

export enum PageStatus {
    CREATED = 'CRIADA',
    STARTED = 'INICIADA',
    FINISHED = 'FINALIZADA',
}

export interface RestockingLists {
  createdAt: Date;
  finishedAt: Date | null;
  id: number;
  idFulfillmentCenter: number;
  type: string;
  level: string;
  priority: string;
  sourceType: string;
  startedAt: Date | null;
  status: string;
  userCreated: string;
}

export interface RestockingListPages {
    createdAt: Date;
    finishedAt: Date;
    id: number;
    keeper: string;
    startedAt: Date;
    status: string;
    totalSkus: number;
}

export interface SetRestockingLists {
    restockingLists: RestockingState['restockingLists'];
}

export interface SetAvailableRestockLists {
    availableRestockLists: RestockingState['availableRestockLists'];
}

export interface SetRestockingListPages {
    restockingListPages: RestockingState['restockingListPages'];
}

export interface RestockingItemsList {
    id: number
    createdAt: Date
    finishedAt?: Date
    idFulfillmentCenter: number
    qtySku: number
    status: PageStatusFilters
    startedAt: Date
    keeper?: string
    generatedCode: string
    totalItems: number
    items: ItemsList[]
    totalSkus: number
    sourceType: string
    type: string
}

export interface ItemsList {
    productCode: string
    productDescription: string
    street: string
    idModel: number
    minimumExpirationDate: Date
    gondolaAddress: string
    restockQuantity: number
    addresses: SourceAddresses[]
}

export interface ItemsListPayload {
    ids: number[]
}

export interface SetRestockingItemsList {
    restockingItemsList: RestockingState['restockingItemsList'];
}

export interface RestockingInfo {
    startedPage: StartedPage;
    itemsToRestock: ItemToRestock[]
}

export interface ValidateBadgePayload {
    badge: string
    history?: History
    createList?: boolean
    setError: (error: string) => void,
    onSuccess: (arg?: RestockingInfo) => void
}

export interface UpdateRestockingListPayload {
    badge: string
    idList: number
}

export enum RestockingListTypesEnum {
    RESTOCKING_GROCERY = 1,
    RESTOCKING_FRESH = 2,
    MISSING_GROCERY = 3,
    MISSING_FRESH = 4,
    CHECK_IN_GROCERY = 5,
    CHECK_IN_FRESH = 6,
    CORRECTIVE = 7,
    WINES = 8,
    ON_DEMAND = 9,
    DRY_PICKING = 10,
    DRY_PICKING_RETURN = 11,
    TRANSFER = 12,
    READDRESSING = 13,
    REQUESTING_SUPPLIES = 14,
    RESTOCKING_FRESH_AUTOMATIC = 15,
    TRANSFER_LOSS_PREVENTION = 16
}

export interface GetProductsListByCSVApiParams {
    csvFile: File
    idType: number
}

export type GetProductsListByCSVSagaParams = {
    setCsvFileAlreadySended: React.Dispatch<React.SetStateAction<boolean>>
    setCsvFileErrors: React.Dispatch<React.SetStateAction<string>>
} & GetProductsListByCSVApiParams

export interface SearchRestockListsPayload {
    idFulfillmentCenter: number | null
    createdDate: Date | null
    priority: string[]
    sourceType: string[]
    level: string[]
    idType: number[]
    idStatus: number[]
}

export interface SetFiltersSearchLists {
    filtersSearchLists: RestockingState['filtersSearchLists'];
}

export interface SetSelectedFiltersHistory {
    selectedFiltersHistory: RestockingState['selectedFiltersHistory'];
}

export interface RemoveRestockingItemPayload {
    commonId: number
    idToDelete: number
    itemName: string
    stateName: keyof RestockingState
    endpoint?: string
    history: History
}

export interface RemoveRestockingItemFromState {
    id: number
    stateName: keyof RestockingState
}

export type CompoundType = RestockingLists | RestockingListPages

export interface WarningDeleteRestockingList {
    status: string
    list: RestockingLists
    warning: string
}

export interface GetAvailableRestockListsPayload {
    idFulfillmentCenter: number
    idType: number[]
    hasForkliftAuth: boolean
    badge: string
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export interface AvailableRestockLists {
    id: number
    priority: string
    sourceType: string
    level: string
    pagesNotAvailable: number
    totalPages: number
    createdAt: Date
    type: string
    orderCode: string
}

export interface SetProperty {
    property: keyof RestockingState
    value: RestockingState[keyof RestockingState]
}

export interface StartRestockingListPagePayload {
    idList: number
    badge: string
    history: History
    acceptPaused?: boolean
    onError?: () => void
}

export interface StartRestockingItemAddress {
    idItem: number
    idsItemAddress: number[]
}

export interface StartRestockingAddressPayload {
    itemsAddresses: StartRestockingItemAddress[]
    badge: string
    idPage: number
    idFulfillmentCenter: number
    onSuccess: () => void
}

export interface StartOrFinishRestockingAddressRestockPayload {
    badge: string
    idItem?: number
    idModel?: number
    itemsToFinish?: ItemToRestock[]
    onSuccess: () => void
    history: History
}

export interface ItemsToCollect {
    id: number
    totalSkus: number
    totalItems: number
    addresses: CollectRestockingAddress[]
    entryCollectAddressRequired: boolean
    entryCollectAddress: string | null
}

export interface StartedPage {
    itemsToCollect: ItemsToCollect | null
    pausedPage: boolean
}

export interface FinishedPage {
    startedAt: Date
    finishedAt: Date
    totalItems: number
    totalItemsToRestock: number
    totalVolume: number
    message?: string
}

export interface ResponseStartedPage extends OperationData {
    pausedPage: boolean
    idRestockingType: number
    restockingConfig: RestockingConfig
}

export interface CollectRestockingAddress {
    address: string
    isGondola: boolean
    order: number
    items: ItemRestocking[]
    done: boolean
    started: boolean
}

export interface ItemRestocking {
    id: number
    idModel: number
    barcode: string
    description: string
    restockQuantity: number
    minimumExpirationDate: Date
    destinationAddress: string
    itemAddress: ItemRestockingAddress
    batches: RestockingBatch[]
    done: boolean
    dueDateType: string
}

export interface ItemRestockingAddress {
    ids: number[]
    sourceAddress: string
    quantity: number
    expirationDate: Date
    unfit: boolean
}

export interface RestockingBatch {
    id: number | null
    idModel: number
    quantity: number
    expirationDate: Date
    done: boolean
}

export interface FinishItemAddress {
    idItem: number
    idsItemAddress: number[]
    quantity: number
    quantityToCollect: number
}

export interface FinishRestockingAddressPayload {
    idPage: number
    itemAddress: FinishItemAddress
    badge: string
    restockingBatchIsDone?: SetRestockingBatchIsDonePayload
    addressOrder?: number
    idModel?: number
    idFulfillmentCenter: number
    history: History
    setIsDisabled?: React.Dispatch<React.SetStateAction<boolean>>
}

export interface SetRestockingBatchIsDonePayload {
    addressOrder: number
    idBatch: number | null
}

export interface SetRestockingItemIsDonePayload {
    addressOrder: number
    idModel?: number
}

export interface LocationToRestock {
    idLocation: number
    address: string
    isGondola: boolean
    isFresh: boolean
    order: number
}

export interface BatchToRestock {
    idPageItemAddress: number
    idBatch: number
    batchNumber: number
    quantity: number
    expirationDate: Date
}

export interface ItemToRestock {
    idPageItem: number
    idPage: number
    idModel: number
    barcode: string
    description: string
    location: LocationToRestock
    batches: BatchToRestock[]
    done: boolean
    totalToRestock: number
    started: boolean
}

export enum OperationDataTypeEnum {
    COLLECT = 'collect',
    RESTOCK = 'restock',
    FINISHED = 'finished'
}

export interface OperationData {
    type: OperationDataTypeEnum | null
    item: ItemsToCollect | ItemToRestock[] | FinishedPage | null
}

export interface RestockingFullcartPayload {
    badge: string
    idPage: number
    idsItemAddress: number[]
    history: History
}

export interface OperatorUnlinkPayload {
    badge: string
    idPage: number
    onSuccess: () => void
}

export interface FinishItemRestockUnfit {
    idFulfillmentCenter: number
    idPage: number
    idItem: number
    quantity: number
    badge: string
    history: History
}

export interface ValidateProductPayload {
    idModel: number
    productBarcode: string
    idPage?: number | null
    badge: string
    onSuccess: () => void
}

export interface ValidateRestockAddressPayload {
    badge: string
    idPageItem: number
    address: string
    isGondola: boolean
    idFulfillmentCenter: number
    onFail: () => void
}

export interface ReportItemQuantityPerBoxPayload {
    badge: string
    idItem: number
    newQuantityPerBox: number
    history: History
}

export interface PageItemAddressDetails {
    address: string
    idBatch: number
    inputQuantity: number
    doneQuantity?: number
    dueDate: string
    userStarted?: string
    startedAt?: string
    userFinished?: string
    finishedAt?: string
    status: string
    observation?: string
}

export interface PageItemDetails {
    idItem: number
    idModel: number
    productCode: string
    productDescription: string
    restockQuantity: number
    originalRestockQuantity?: number
    quantityPerBox?: number
    minimumExpirationDate: string
    destinationAddress: string
    totalCollectedQuantity: number
    totalRestockedQuantity: number
    addressesCollect: PageItemAddressDetails[]
    addressesRestock: PageItemAddressDetails[]
}

export interface RestockingPageDetails {
    idList: number
    idPage: number
    userStarted?: string
    startedAt?: string
    userFinished?: string
    finishedAt?: string
    totalItems: number
    totalSkus: number
    status: string
    totalCollectedQuantity: number
    totalRestockedQuantity: number
    items: PageItemDetails[]
}

export interface SetRestockingPageDetails {
    restockingPageDetails: RestockingState['restockingPageDetails'];
}

export enum PageItemAddressDetailsEnum {
    COLLECT = 'collect',
    RESTOCK = 'restock'
}

export interface RestockingListType {
    id: number
    description: string
    source: string
    destination: string
}

export enum RestockingMovementTypesEnum {
    GROCERY = 'MERCEARIA',
    FRESH = 'FRESH',
    FREE = 'LIVRE',
}

export interface CheckInValidatePayload {
    badge: string
    orderBoxCode: string
    idRestockingType: number
}

export interface CheckinGenerateRestockListPayload {
    badge: string
    idType: number
    ordersCode: string[]
    idFulfillmentCenter: number
    onSuccess: (errors: CheckinGenerateRestockListError[]) => void
    onError: (errors: CheckinGenerateRestockListError[]) => void
}

export interface CheckinGenerateRestockListError {
    orderCode: string
    conveyorBelt: string
    errorCode: string
    message: string
}

export interface CheckinGenerateRestockListResponse {
    restockingListId: number
    errors: CheckinGenerateRestockListError[]
}

export interface ValidateCollectAddressPayload {
    badge: string
    idsModels: number[]
    address: string
    addressToValidate: string
    isGondola: boolean
    onSuccess: () => void
    setError: (error: string) => void
}

export interface NewBatchForEmptyGondola {
    quantity: number
    expirationDate: Date
}

export interface FinishCollectAddressEmptyGondolaPayload {
    idFulfillmentCenter: number
    idPage: number
    idItem: number
    idItemAddress: number
    quantityToCollect: number
    batches: NewBatchForEmptyGondola[]
    badge: string
    restockingBatchIsDone?: SetRestockingBatchIsDonePayload
    idModel: number
    history: History
    setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>
}

export interface RestockingValidateEntryCollectAddressPayload {
    badge: string
    idPage: number
    address: string
    history: History
    setDisabled: React.Dispatch<React.SetStateAction<boolean>>
}

export interface GetMovingAddressPayload {
    addressType: number[];
    fc: number[];
    availability: string;
}

export interface MovingAddress {
    idLocation: number
    address: string
    fc: string
    hasStock: boolean
    isBlocked: boolean
    isAvailable: boolean
    idLocationType: number
    idPage: number | null
    userName: string | null
    usingTime: string | null
    listTypeName: string | null
}

export interface PrintAddressesLabelPayload {
    addresses: string[]
    onFinally: () => void
}

export enum LocationTypeEnum {
    TRANSFER_CAGE = 5,
    MOVEMENT = 6
}
